import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Tooltip, OverlayTrigger, Accordion, Form } from 'react-bootstrap';
import _ from 'lodash';
import TimePicker from 'react-time-picker';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import ErrorStackParser from 'error-stack-parser';

import '../../util/moment-duration';
import windowSize from '../../util/windowSize';
import dateUtils from '../../util/dates';
import FormattedMessage from '../FormattedMessage';
import Checkbox from '../Checkbox/Checkbox';
import Switch from '../Switch/Switch';
import RadioButton from '../RadioButton/RadioButton';
import throttle from '../../util/throttle';
import logger from '../../util/logger';
import { hasLabels, areMembersAllowed } from '../../util/customFieldsUtils';

import InsightsTrial from '../../assets/images/Insights-trial.svg';

import { trackHowToOpened } from '../../util/mixpanel';

import {
  initFilters,
  updateValues,
  saveConfig,
  saveGlobalConfig,
  updateTrendLine,
  updateSelectedDate,
  updateEnableZero,
  updateEnabledColors,
  updateWipStagesTooltip,
  updateApproxAvgStagesTooltip,
  updateArrivalDepartureLines,
  updateExcludeWeekends,
  updateActivePanel,
  updateWorkHours,
  updateHolidays,
  updateDueDateTasks,
  updateExpandedFilters,
  updateShowColumns,
  updateBlockers,
  updateBlockedTime,
  saveFilterSize,
  handleError
} from './FilterActions';

import {
  LocalLists,
  QueueLists,
  LocalLabels,
  LocalMembers,
  LocalDoneStatuses,
  LocalPercentiles,
  LocalPercentileTrends,
  LocalCustomField,
  DateFormat,
  TimePrecision,
  TimeUnit,
  TimeUnitBreakdown,
  BreakdownBy,
  WipBreakdownBy,
  MonteCarloDeliveryControls,
  MonteCarloTasksControls,
  ScaleSlider,
  LocalHealthZones,
  EnableColors,
  EfficiencyRange,
  DateSlider,
  ActiveFiltersCounter,
  BlockedTimeControls,
  WidgetSelector
} from './components';

import Insights from '../Insights/Insights';
import { FILTER_MIN_WIDTH, getFilterMaxWidth } from '../../modules/Common/globals';

// Import Style
import './Filter.scss';

export class Filter extends Component {
  static defaultProps = {
    disable: [],
    disabledLists: []
  };

  constructor(props) {
    super(props);

    this.state = {
      pos: null,
      dragging: false,
      moved: false,
      rel: null,
      calendarOpened: false,
      calendarPos: 'up',
      allExpanded: [],
      customizeViewActive: false,
      customizeExpanded: ['customizeControls'],
      selectedWidgets: [],
    };

    this.calendarRef = React.createRef();
    this.handleRef = React.createRef();
    this.handleChange = throttle(this._handleChange, 1000, this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const params = { error, info, stackframes: ErrorStackParser.parse(error) };
    logger.report(params);
    console.error(error, info);
  }

  componentDidMount() {
    this.props.initFilters({
      todo: this.props.disabledLists.indexOf('todo') > -1,
      done:
        (this.props.user.config && !this.props.user.config.enableDoneFilters) ||
        (this.props.user.config &&
          !!this.props.user.config.enableDoneFilters &&
          this.props.disabledLists.indexOf('done') > -1)
    }, this.props.isProcessImprovement);
    if (this.props.windowWidth) {
      this.handleScreenResize();
    }
    document.addEventListener('mousedown', this.handleClickOutside);
    this.rememberPanelTabs();

    if (this.props.expanded) {
      this.setState({
        allExpanded: this.props.expanded
      })
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.documentElement.style.removeProperty('--filter-width');
  }

  rememberPanelTabs() {
    try {
      window.localStorage.setItem('panelTabs', this.props.user?.config?.aiInsightsStatus ? '1' : '0');
    } catch (err) {}
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.user !== this.props.user) {
      this.rememberPanelTabs();
    }
    if (prevProps.windowWidth !== this.props.windowWidth || this.props.activePanel !== prevProps.activePanel) {
      this.handleScreenResize();
    }

    if (prevProps.filtersDataHash != this.props.filtersDataHash) {
      this.props.updateValues(this.props.updatedField, this.props.isProcessImprovement);
    }

    if (prevProps.expanded != this.props.expanded || prevProps.chart != this.props.chart) {
      this.setState({
        allExpanded: this.props.expanded
      });
    }

    if (this.state.dragging && !prevState.dragging) {
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    } else if (!this.state.dragging && prevState.dragging) {
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
    }
  }

  handleScroll = ev => {
    const dateRangePicker = this.calendarRef.current;  // Access DateRangePicker
    if (dateRangePicker) {
      const rect = dateRangePicker.getBoundingClientRect(); // Get the position of DateRangePicker
      const viewportHeight = window.innerHeight;
      const spaceAbove = rect.top + rect.height;
      const spaceBelow = viewportHeight - rect.bottom;

      // Decide on calendar dropdown position based on available space
      const calendarPos = spaceBelow >= 300 ? 'down' : spaceAbove >= 300 ? 'up' : 'down';

      this.setState({
        calendarPos,
      });
    }
  };

  handleShowDatePicker = () => {
    this.setState({
      calendarOpened: true
    });
  };

  handleHideDatePicker = () => {
    this.setState({
      calendarOpened: false
    });
  };

  handleScreenResize = () => {
    if (this.props.windowWidth > 992) {
      const size =
        this.props.user.config && this.props.user.config.filterSize ? this.props.user.config.filterSize : FILTER_MIN_WIDTH;
      this.updateSizes(Math.max(FILTER_MIN_WIDTH, Math.min(window && window.filterWidth ? window.filterWidth : size, getFilterMaxWidth())));
    } else {
      this.updateSizes();
    }
  };

  onMouseDown = e => {
    // only left mouse button
    if (e.button !== 0) {
      return false;
    }

    var pos = this.handleRef.current.getBoundingClientRect();
    this.setState({
      dragging: true,
      moved: false,
      rel: {
        x: e.pageX - pos.left,
        y: e.pageY - pos.top
      }
    });
    e.stopPropagation();
    e.preventDefault();
  };

  onMouseUp = e => {
    const { moved, right } = this.state;
    this.setState({ dragging: false, moved: false, right: null });
    e.stopPropagation();
    e.preventDefault();
    if (window && window.hcEvents && window.hcEvents.resize) {
      for (let i = 0; i < window.hcEvents.resize.length; i++) {
        const handler = window.hcEvents.resize[i];
        (handler.fn || handler)();
      }
    }

    if (moved && right) {
      this.props.saveFilterSize(right);
    }
  };

  onMouseMove = e => {
    if (!this.state.dragging) {
      return false;
    }

    const right = Math.max(FILTER_MIN_WIDTH, Math.min(window.innerWidth - e.pageX - this.state.rel.x, getFilterMaxWidth()));
    this.setState({
      moved: true,
      right,
      pos: {
        x: e.pageX - this.state.rel.x,
        y: e.pageY - this.state.rel.y
      }
    });

    this.updateSizes(right);
    e.stopPropagation();
    e.preventDefault();
  };

  updateSizes = width => {
    const filter = document.getElementsByClassName('filter');
    const rightPartHeader = document.getElementsByClassName('top_header');
    const notificationBar = document.getElementsByClassName('notification_bar');
    const process = document.getElementsByClassName('process');
    const overview = document.getElementsByClassName('overview');
    const chartBox = document.getElementsByClassName('chart_box_view');
    const insights = document.getElementsByClassName('insights_container');

    if (width >= FILTER_MIN_WIDTH && width <= getFilterMaxWidth()) {
      window.filterWidth = width;
      document.documentElement.style.setProperty('--filter-width', `${width}px`);
      try {
        window.localStorage.setItem('filterWidth', String(width));
      } catch (err) {}
      for (let i = 0; i < filter.length; i++) {
        filter[i].style.width = `${width}px`;
      }
      for (let i = 0; i < rightPartHeader.length; i++) {
        rightPartHeader[i].style.marginRight = `${width}px`;
      }
      for (let i = 0; i < notificationBar.length; i++) {
        notificationBar[i].style.marginRight = `${width}px`;
      }
      for (let i = 0; i < chartBox.length; i++) {
        chartBox[i].style.marginRight = `${width}px`;
      }
      for (let i = 0; i < overview.length; i++) {
        overview[i].style.marginRight = `${width}px`;
      }
      for (let i = 0; i < process.length; i++) {
        process[i].style.marginRight = `${width}px`;
      }
      for (let i = 0; i < insights.length; i++) {
        insights[i].style.marginRight = `${width}px`;
      }
    } else if (!width) {
      window.filterWidth = null;
      document.documentElement.style.removeProperty('--filter-width');
      for (let i = 0; i < filter.length; i++) {
        filter[i].style.width = ``;
      }
      for (let i = 0; i < rightPartHeader.length; i++) {
        rightPartHeader[i].style.marginRight = ``;
      }
      for (let i = 0; i < notificationBar.length; i++) {
        notificationBar[i].style.marginRight = `inherit`;
      }
      for (let i = 0; i < chartBox.length; i++) {
        chartBox[i].style.marginRight = `inherit`;
      }
      for (let i = 0; i < overview.length; i++) {
        overview[i].style.marginRight = `inherit`;
      }
      for (let i = 0; i < process.length; i++) {
        process[i].style.marginRight = `inherit`;
      }
      for (let i = 0; i < insights.length; i++) {
        insights[i].style.marginRight = `inherit`;
      }
    }
  };

  _handleChange = (name, e) => {
    if (!e) {
      return false;
    }

    const newTime = new moment(e, 'HH:mm');

    switch (name) {
      case 'workStart': {
        const endTime = new moment(this.props.workEnd, 'HH:mm');
        if (newTime.isSame(endTime, 'minute')) {
          this.props.handleError(`Starting time should not be equal to Finishing time`);
          return false;
        }
        if (newTime.isAfter(endTime, 'minute')) {
          this.props.handleError(`Starting time should be before Finishing time`);
          return false;
        }
        this.props.updateWorkStart(e);
        break;
      }
      case 'workEnd': {
        const startTime = new moment(this.props.workStart, 'HH:mm');
        if (newTime.isSame(startTime, 'minute')) {
          this.props.handleError(`Finishing time should not be equal to Starting time`);
          return false;
        }
        if (newTime.isBefore(startTime, 'minute')) {
          this.props.handleError(`Finishing time should be after Starting time`);
          return false;
        }
        this.props.updateWorkEnd(e);
        break;
      }
    }
    this.props.saveGlobalConfig();
  };

  handleCheckboxChangeGlobal = e => {
    switch (e.name) {
      case 'excludeWeekends': {
        this.props.updateExcludeWeekends(e.value);
        break;
      }
    }
    this.props.saveGlobalConfig();
  };

  handleCheckboxChange = e => {
    switch (e.name) {
      case 'excludeZeroDays': {
        this.props.updateEnableZero(e.checked);
        break;
      }
      case 'wipStagesTooltip': {
        this.props.updateWipStagesTooltip(e.value);
        break;
      }
      case 'approxAvgStagesTooltip': {
        this.props.updateApproxAvgStagesTooltip(e.value);
        break;
      }
      case 'arrivalDepartureLines': {
        this.props.updateArrivalDepartureLines(e.checked);
        break;
      }
      case 'trendLine': {
        this.props.updateTrendLine(e.checked);
        break;
      }
      case 'dueDateTasks': {
        this.props.updateDueDateTasks(e.value);
        break;
      }
      case 'selectedDate': {
        this.props.updateSelectedDate(e.value);
        break;
      }
      case 'showColumns': {
        this.props.updateShowColumns(e.checked);
        break;
      }
      case 'blockers': {
        this.props.updateBlockers(e.checked);
        if (!e.checked) {
          this.props.updateBlockedTime('all');
        }
        break;
      }
    }
    this.props.saveConfig();
  };

  handleAddDate = (event, picker) => {
    let dates = [...this.props.holidays];
    dates.push(moment(picker.startDate).toISOString());
    dates = _(dates)
      .orderBy()
      .uniq()
      .value();

    this.props.updateHolidays(dates);
    this.props.saveGlobalConfig();
  };

  handleDateRemove = date => {
    const dates = [...this.props.holidays];
    _.remove(dates, d => {
      return d === date;
    });

    this.props.updateHolidays(dates);
    this.props.saveGlobalConfig();
  };

  accordionChanged = (level, e, ev) => {
    let currentExpanded = e;

    if (level === 'filters') {
      const prevFilters = this.state.allExpanded;

      //custom fields change
      if (prevFilters.indexOf('cf_') > -1 && e.indexOf('cf_') === -1) {
        currentExpanded = this.getFilterKeysWithCf(prevFilters, ev) || e
      }
    }

    this.setState({
      allExpanded: currentExpanded
    }, () => {
      const expanded = this.state.allExpanded;
      const update = !_.isEqual(expanded.sort(), this.props.expanded.sort());
      this.props.updateExpandedFilters(expanded);
      update && this.props.saveConfig();
    });
  };

  getFilterKeysWithCf = (prevFilters, ev) => {
    const { customFilterKeys } = this.props;
    const currentCfKey = ev && ev.target && ev.target.parentElement && ev.target.parentElement.parentElement ?
      ev.target.parentElement.parentElement.getAttribute('cf_key') : null;

    if (currentCfKey != null) {
      return prevFilters.filter(key => key !== 'cf_').concat(customFilterKeys.filter(key => key !== currentCfKey));
    }

    return null;
  }

  trackHowToXAxisOpen = () => {
    trackHowToOpened('x-axis');
  }

  handleCustomizeViewClick = () => {
    if (this.props.onCustomizeViewClick) {
      this.props.onCustomizeViewClick();
    }
  };

  handleGoBackFromCustomize = () => {
    if (this.props.onGoBackFromCustomize) {
      this.props.onGoBackFromCustomize();
    }
  };

  handleApplyCustomization = () => {
    if (this.props.onApplyCustomization) {
      this.props.onApplyCustomization();
    }
  };

  handleToggleActiveTab = () => {
    const { activePanel } = this.props;
    this.props.updateActivePanel(activePanel === 'insights' ? 'filters' : 'insights');
    this.props.saveGlobalConfig();
  }

  render() {
    const { user, activePanel } = this.props;

    const aiInsightsStatus = user?.config?.aiInsightsStatus;
    const aiInsightsTrial = aiInsightsStatus === 'trial';

    if (!aiInsightsStatus) {
      return (
        <div className='resize_container'>
          <div className='resize_handle' onMouseDown={this.onMouseDown} ref={this.handleRef} />
          { this.renderFilter() }
        </div>
      )
    }

    return (
      <div className='resize_container'>
        <div className='resize_handle' onMouseDown={this.onMouseDown} ref={this.handleRef} />
        <div className="overview-button-group">
          <Button
            variant="secondary"
            className={activePanel === 'insights' ? 'selected' : ''}
            onClick={activePanel === 'insights' ? undefined : this.handleToggleActiveTab}
          >
            Insights
            {' '}
            {aiInsightsTrial && <span className="badge">Trial</span>}
          </Button>
          <Button
            variant="secondary"
            className={activePanel === 'filters' ? 'selected' : ''}
            onClick={activePanel === 'filters' ? undefined : this.handleToggleActiveTab}
          >
            Filter
          </Button>
        </div>
        { activePanel  === 'insights' ? this.renderInsights() : this.renderFilter() }
      </div>
    );
  }

  renderInsights() {
    const { user } = this.props;

    const isTrialExpired = user?.config?.aiInsightsTrialEnd && moment(user.config.aiInsightsTrialEnd).isBefore(moment());

    if (isTrialExpired) {
      const quoteBody = `Hi Nave team,\nI’d like a pricing quote for Nave Insights.\n\nAccount: ${user.email}\nNumber of dashboards: ${this.props.dashboardsCount}`;
      const quoteHref = `mailto:hello@getnave.com?subject=${encodeURIComponent('Nave Insights pricing request')}&body=${encodeURIComponent(quoteBody)}`;

      return (
        <div className="filter">
          <div className="insights-trial">
            <div className="image"><img src={InsightsTrial} alt=""/></div>
            <h6>Your Insights trial has ended</h6>
            <p>Keep finding what’s slowing delivery down and where your attention is needed.</p>
            <a className="btn btn-primary btn-request" href={quoteHref}>Request a quote</a>
          </div>
        </div>
      );
    }

    return (
      <div className="filter">
        <Insights />
      </div>
    );
  }

  renderFilter() {
    if (this.state.hasError) {
      return <span></span>;
    }

    const {
      chart,
      expanded,
      customFields,
      isOverview,
      enableTrendline,
      enableSelectedDate,
      enableMarkBlocked,
      enablePercentiles,
      enablePercentileTrends,
      enableHealthZones,
      enableQueueLists,
      montecarlo,
      montecarlotasks,
      excludeZero,
      tasksType,
      timeunit,
      tooltipoptions,
      arrivaldeparturelines,
      colors,
      excludeZeroDays,
      wipStagesTooltip,
      approxAvgStagesTooltip,
      disable,
      arrivalDepartureLines,
      selectedDate,
      trendLine,
      markBlocked,
      excludeWeekends,
      dateSlider,
      dueDateTasks,
      enableBreakdownBy,
      breakdownByText,
      enableWipBreakdownBy,
      workStart,
      workEnd,
      doneStatus,
      dashboard
    } = this.props;

    const app = dashboard && dashboard.application || this.props.app;

    const showMarkBlocked =
      app == 'trello' || app == 'azure'
        ? enableMarkBlocked && dashboard.selectedBlockedFlag
        : app === 'jira' && dashboard.isBusiness && !dashboard.selectedBlockedFlag
        ? false
        : enableMarkBlocked;

    const fileApp = (app === 'file' && !dashboard.isCsv) && (dashboard.isAzure ? 'azure' : 'jira');

    const getTotalItemsText = () => {
      const { totalItems } = this.props;
      if (typeof totalItems === 'undefined') {
        return '';
      }
      return (totalItems === 0 ? 'No' : totalItems) + ' ' +
        {
          'asana': 'task',
          'azure': 'work item',
          'file': 'work item',
          'jira': 'work item',
          'trello': 'card',
        }[app] + (totalItems > 1 || totalItems === 0 ? 's' : '');
    }

    const queueListsTooltip = (
      <Tooltip id="queuesTooltip" className='tooltip_white max_width_200'>
        Queue states are states with no activity where work is sitting idle
      </Tooltip>
    );
    const blockedTooltip = (
      <Tooltip id="blockedIssuesTooltip" className='tooltip_white max_width_200'>
        This control lets you choose whether to see the entire time spent on the work, only the time when the work was blocked, or only the time when it was moving without being blocked.
      </Tooltip>
    );
    const showBlockersTooltip = (
      <Tooltip id="showBlockersTooltip">
        {this.props.blockers ? 'Blocked items highlighted in red' : 'Items shown in original colors'}
      </Tooltip>
    );
    const xAxisTooltip = (
      <Tooltip id="xAxisTooltip" className='tooltip_white max_width_200'>
        How to use the{' '}
        <a
          href={`https://getnave.com/blog/impact-of-implementing-wip-limits/`}
          onClick={this.trackHowToXAxisOpen}
          onAuxClick={this.trackHowToXAxisOpen}
          target="_blank"
        >
          X-axis feature
        </a>
      </Tooltip>
    );
    const showColumnsTooltip = (
      <Tooltip id="showColumnsTooltip" className='tooltip_dark text-align-left'>
        <div>On: Keeps all cards. Time spent in deselected statuses is removed.</div>
        <div>Off: Deselected columns are hidden and cards in those columns are removed.</div>
      </Tooltip>
    );

    return (
      <React.Fragment>
        {!this.props.customizeMode ?
          <Accordion alwaysOpen='true'
            onScroll={this.handleScroll}
            onSelect={e => this.accordionChanged('root', e)}
            className={`filter filter-panel ${this.state.calendarOpened && 'no-scroll'} ${!!this.state.overview && 'filter-customize'}`}
            activeKey={this.state.allExpanded}
          >
            <Accordion.Item
              uuid="controls"
              eventKey="controls"
            >
              <Accordion.Header>
                <React.Fragment>
                  <span className="title">Controls</span>
                </React.Fragment>
              </Accordion.Header>
              <Accordion.Body>
                <Accordion
                  alwaysOpen
                  onSelect={e => this.accordionChanged('controls', e)}
                  activeKey={this.state.allExpanded}
                >
                  {dateSlider && (
                    <Accordion.Item
                      uuid="aging_replay"
                      eventKey="aging_replay"
                    >
                      <Accordion.Header>
                        <React.Fragment>Aging replay</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <DateSlider />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {montecarlo && (
                    <Accordion.Item
                      uuid="mc_sim_ctrls"
                      eventKey="mc_sim_ctrls">
                      <Accordion.Header>
                        <React.Fragment>Simulation controls</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <MonteCarloDeliveryControls />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {montecarlotasks && (
                    <Accordion.Item
                      uuid="mct_sim_ctrls"
                      eventKey="mct_sim_ctrls"
                    >
                      <Accordion.Header>
                        <React.Fragment>Simulation controls</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <MonteCarloTasksControls />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {(montecarlo || montecarlotasks) && (
                    <Accordion.Item
                      uuid="scale"
                      eventKey="scale"
                    >
                      <Accordion.Header>
                        <React.Fragment>Scale</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <ScaleSlider />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {enableQueueLists && (
                    <Accordion.Item
                      uuid="queue_states"
                      eventKey="queue_states"
                    >
                      <Accordion.Header>
                        <React.Fragment>
                          Queue states
                          <OverlayTrigger placement="top" overlay={queueListsTooltip} delay={{ show: 250, hide: 2000 }}>
                            <span className="more_info" />
                          </OverlayTrigger>
                        </React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <QueueLists />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {enableQueueLists && (
                    <Accordion.Item
                      uuid="percent_range"
                      eventKey="percent_range"
                    >
                      <Accordion.Header>
                        <React.Fragment>Percent range</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <EfficiencyRange />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {enableSelectedDate && (
                    <Accordion.Item
                      uuid="selected_date"
                      eventKey="selected_date"
                    >
                      <Accordion.Header>
                        <React.Fragment>
                          X-axis
                          <OverlayTrigger placement="top" overlay={xAxisTooltip} delay={{ show: 250, hide: 2000 }}>
                            <span className="more_info" />
                          </OverlayTrigger>
                        </React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div>
                          <RadioButton
                            label="Start date"
                            id="selected_date"
                            name="selectedDate"
                            value="start"
                            checked={selectedDate == 'start'}
                            onChange={this.handleCheckboxChange}
                          />
                          <RadioButton
                            label="End date"
                            id="selected_date"
                            name="selectedDate"
                            value="end"
                            checked={selectedDate == 'end'}
                            onChange={this.handleCheckboxChange}
                          />
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                {enableTrendline && (
                  <Accordion.Item
                    uuid="trendlines"
                    eventKey="trendlines"
                  >
                    <Accordion.Header>
                      <React.Fragment>Trendline</React.Fragment>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div>
                        <Checkbox
                          name="trendLine"
                          id="trendLine"
                          key="1"
                          label="Enabled"
                          checked={!!trendLine}
                          onChange={this.handleCheckboxChange}
                        />
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                  {showMarkBlocked && (
                    <Accordion.Item
                      uuid="blocked_issues"
                      eventKey="blocked_issues"
                    >
                      <Accordion.Header>
                        <React.Fragment>
                          Blocker
                          <OverlayTrigger placement="top" overlay={blockedTooltip} delay={{ show: 250, hide: 2000 }}>
                            <span className="more_info" />
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={showBlockersTooltip} delay={{ show: 250, hide: 400 }}>
                            <span style={{ float: 'right', display: 'flex', alignItems: 'center' }}>
                              <Switch
                                id="blockers"
                                className="switch_box"
                                name="blockers"
                                checked={!!this.props.blockers}
                                onChange={this.handleCheckboxChange}
                              />
                            </span>
                          </OverlayTrigger>
                        </React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div style={{
                          opacity: this.props.blockers ? 1 : 0.5,
                          pointerEvents: this.props.blockers ? 'auto' : 'none'
                        }}>
                          <BlockedTimeControls />
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                {excludeZero && (
                  <Accordion.Item
                    uuid="excl_zeros"
                    eventKey="excl_zeros"
                  >
                    <Accordion.Header>
                      <React.Fragment>Exclude zero values</React.Fragment>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div>
                        <Checkbox
                          name="excludeZeroDays"
                          id="excludeZeroDays"
                          key="1"
                          label="Enabled"
                          checked={!!excludeZeroDays}
                          onChange={this.handleCheckboxChange}
                        />
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                  {doneStatus && this.props.user && this.props.user.config.enableDoneFilters && (
                    <Accordion.Item
                      uuid="done_status"
                      eventKey="done_status"
                    >
                      <Accordion.Header>
                        <React.Fragment>Done status</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <LocalDoneStatuses />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {tasksType && (
                    <Accordion.Item
                      uuid="tasks_type"
                      eventKey="tasks_type"
                    >
                      <Accordion.Header>
                        <React.Fragment>Delivery</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div>
                          <RadioButton
                            label="All"
                            id="tasks_type"
                            name="dueDateTasks"
                            value="all"
                            checked={dueDateTasks == 'all'}
                            onChange={this.handleCheckboxChange}
                          />
                          <RadioButton
                            label="On time work items"
                            id="tasks_type"
                            name="dueDateTasks"
                            value="due"
                            checked={dueDateTasks == 'due'}
                            onChange={this.handleCheckboxChange}
                          />
                          <RadioButton
                            label="Overdue work items"
                            id="tasks_type"
                            name="dueDateTasks"
                            value="overdue"
                            checked={dueDateTasks == 'overdue'}
                            onChange={this.handleCheckboxChange}
                          />
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {enableHealthZones && (
                    <Accordion.Item
                      uuid="health_zones"
                      eventKey="health_zones"
                    >
                      <Accordion.Header>
                        <React.Fragment>Health zones</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <LocalHealthZones />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {enablePercentiles && (
                    <Accordion.Item
                      uuid="percentiles"
                      eventKey="percentiles"
                    >
                      <Accordion.Header>
                        <React.Fragment>Percentiles</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <LocalPercentiles />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {enablePercentileTrends && (
                    <Accordion.Item
                      uuid="percentile_trends"
                      eventKey="percentile_trends"
                      className="percentile-trends-item"
                    >
                      <Accordion.Header>
                        <React.Fragment>Calculate percentile trends by</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <LocalPercentileTrends />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {arrivaldeparturelines && (
                    <Accordion.Item
                      uuid="arr_dep_lines"
                      eventKey="arr_dep_lines"
                    >
                      <Accordion.Header>
                        <React.Fragment>Arrival & departure lines</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div>
                          <Checkbox
                            name="arrivalDepartureLines"
                            id="arrivalDepartureLines"
                            key="1"
                            label="Enabled"
                            checked={!!arrivalDepartureLines}
                            onChange={this.handleCheckboxChange}
                          />
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {tooltipoptions && (
                    <Accordion.Item
                      uuid="wip_tooltip"
                      eventKey="wip_tooltip"
                    >
                      <Accordion.Header>
                        <React.Fragment>WIP tooltips</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div>
                          <RadioButton
                            label="States"
                            id="wip-stages"
                            name="wipStagesTooltip"
                            value="stages"
                            checked={wipStagesTooltip == 'stages'}
                            onChange={this.handleCheckboxChange}
                          />
                          <RadioButton
                            label="Process"
                            id="wip-system"
                            name="wipStagesTooltip"
                            value="system"
                            checked={wipStagesTooltip == 'system'}
                            onChange={this.handleCheckboxChange}
                          />
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {tooltipoptions && (
                    <Accordion.Item
                      uuid="ct_tooltip"
                      eventKey="ct_tooltip"
                    >
                      <Accordion.Header>
                        <React.Fragment>Cycle time tooltips</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div>
                          <RadioButton
                            label="States"
                            id="avg-stages"
                            name="approxAvgStagesTooltip"
                            value="stages"
                            checked={approxAvgStagesTooltip == 'stages'}
                            onChange={this.handleCheckboxChange}
                          />
                          <RadioButton
                            label="Process"
                            id="avg-system"
                            name="approxAvgStagesTooltip"
                            value="system"
                            checked={approxAvgStagesTooltip == 'system'}
                            onChange={this.handleCheckboxChange}
                          />
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {timeunit && (
                    <Accordion.Item
                      uuid="group_by"
                      eventKey="group_by"
                    >
                      <Accordion.Header>
                        <React.Fragment>Group by</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        {chart === 'ctb' ? <TimeUnitBreakdown /> : <TimeUnit />}
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {colors && (
                    <Accordion.Item
                      uuid="enabled_color"
                      eventKey="enabled_color"
                    >
                      <Accordion.Header>
                        <React.Fragment>{typeof colors === 'string' ? colors : 'Colors'}</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <EnableColors global={!!isOverview} />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {!(dashboard.application === 'file' && dashboard.isCsv) &&
                    enableBreakdownBy && (
                      <Accordion.Item
                        uuid="breakdown_by"
                        eventKey="breakdown_by"
                      >
                        <Accordion.Header>
                          <React.Fragment> {breakdownByText || 'Breakdown by'}</React.Fragment>
                        </Accordion.Header>
                        <Accordion.Body>
                          <BreakdownBy />
                        </Accordion.Body>
                      </Accordion.Item>
                    )}

                  {!(dashboard.application === 'file' && dashboard.isCsv) &&
                    enableWipBreakdownBy && (
                      <Accordion.Item
                        uuid="wip_breakdown_by"
                        eventKey="wip_breakdown_by"
                      >
                        <Accordion.Header>
                          <React.Fragment>Breakdown by</React.Fragment>
                        </Accordion.Header>
                        <Accordion.Body>
                          <WipBreakdownBy />
                        </Accordion.Body>
                      </Accordion.Item>
                    )}

                  {!dashboard.isCsv &&
                    !!isOverview && [
                      <Accordion.Item key={'opt1'}
                        uuid="ct_precision"
                        eventKey="ct_precision"
                      >
                        <Accordion.Header>
                          <React.Fragment>Cycle time precision</React.Fragment>
                        </Accordion.Header>
                        <Accordion.Body>
                          <TimePrecision />
                        </Accordion.Body>
                      </Accordion.Item>,

                      <Accordion.Item key={'opt2'}
                        uuid="date_format"
                        eventKey="date_format"
                      >
                        <Accordion.Header>
                          <React.Fragment>Date format</React.Fragment>
                        </Accordion.Header>
                        <Accordion.Body>
                          <DateFormat />
                        </Accordion.Body>
                      </Accordion.Item>
                    ]}

                  {!!isOverview && (
                    <Accordion.Item
                      uuid="excl_weekends"
                      eventKey="excl_weekends"
                    >
                      <Accordion.Header>
                        <React.Fragment>Non-working time</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div>
                          <RadioButton
                            label="Disabled"
                            id="none"
                            name="excludeWeekends"
                            value="0"
                            checked={excludeWeekends == 0}
                            onChange={this.handleCheckboxChangeGlobal}
                          />
                          <RadioButton
                            label="Exclude weekends"
                            id="weekends"
                            name="excludeWeekends"
                            value="1"
                            checked={excludeWeekends == 1}
                            onChange={this.handleCheckboxChangeGlobal}
                          />
                          <RadioButton
                            label="Exclude non-working hours"
                            id="nonworking-hours"
                            name="excludeWeekends"
                            value="2"
                            checked={excludeWeekends == 2}
                            onChange={this.handleCheckboxChangeGlobal}
                          />
                          {excludeWeekends == 2 && (
                            <div className='data-form-control'>
                              <div>
                                <label htmlFor="workStart">Starting time</label>
                                <TimePicker
                                  name="workStart"
                                  className='form-control'
                                  value={workStart}
                                  onChange={ev => this.handleChange('workStart', ev)}
                                  disableClock={true}
                                  maxDetail={'minute'}
                                  required={true}
                                  clearIcon={null}
                                  format={'hh:mm a'}
                                />
                              </div>

                              <div>
                                <label  htmlFor="workEnd">Finishing time</label>
                                <TimePicker
                                  name="workEnd"
                                  className='form-control'
                                  value={workEnd}
                                  onChange={ev => this.handleChange('workEnd', ev)}
                                  disableClock={true}
                                  maxDetail={'minute'}
                                  required={true}
                                  clearIcon={null}
                                  format={'hh:mm a'}
                                />
                              </div>
                            </div>
                          )}

                        {(excludeWeekends == 1 || excludeWeekends == 2) && (
                          <div style={{ marginTop: '20px' }}>
                            <label>Add non-working days</label>
                            <div className='dates-list'  ref={this.calendarRef} >
                              {this.props.holidays.map((d, i) => {
                                return (
                                  <span key={i} className='date-label form-control'>
                                    {moment(d).format(this.props.dateFormat)}
                                    <span className='date-label-close' onClick={() => this.handleDateRemove(d)} />
                                  </span>
                                );
                              })}
                              <DateRangePicker
                                drops={this.state.calendarPos}
                                onShow={this.handleShowDatePicker}
                                onHide={this.handleHideDatePicker}
                                locale={{ format: this.props.dateFormat }}
                                singleDatePicker
                                onApply={this.handleAddDate}
                                updateValue={() => {}}
                              >
                                <Button size="md">Add</Button>
                              </DateRangePicker>
                            </div>
                          </div>
                        )}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                )}
              </Accordion>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item
            uuid="filters"
            eventKey="filters"
          >
            <Accordion.Header>
              <React.Fragment><span className="title">Filters</span>{getTotalItemsText() && <span className='filter-count'>{getTotalItemsText()}</span>}</React.Fragment>
            </Accordion.Header>
            <Accordion.Body>
              <Accordion
                alwaysOpen
                activeKey={this.state.allExpanded}
                onSelect={(e, ev) => this.accordionChanged('filters', e, ev)}
              >
                {disable.indexOf('lists') === -1 && (
                  <Accordion.Item eventKey="lists"
                    uuid="lists"
                  >
                    <Accordion.Header>
                      <React.Fragment>
                        <span className='title'><FormattedMessage id={`${app}.list`} mode="capitalize" plural="true" /></span>
                        <ActiveFiltersCounter name={'lists'} />
                        {this.props.enableShowColumns && (
                          <OverlayTrigger placement="top" overlay={showColumnsTooltip} delay={{ show: 250, hide: 400 }}>
                            <span style={{ float: 'right', display: 'flex', alignItems: 'center' }}>
                              <Switch
                                id="showColumns"
                                className="switch_box"
                                name="showColumns"
                                checked={!!this.props.showColumns}
                                onChange={this.handleCheckboxChange}
                              />
                            </span>
                          </OverlayTrigger>
                        )}
                      </React.Fragment>
                    </Accordion.Header>
                    <Accordion.Body>
                      <LocalLists />
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                  {!dashboard.isCsv && hasLabels(app, dashboard.selectedFields) && disable.indexOf('labels') === -1 && (
                    <Accordion.Item eventKey="labels"
                      uuid="labels"
                      className='label_filter'>
                      <Accordion.Header>
                        <React.Fragment>
                          <span className='title'><FormattedMessage id={`${fileApp || app}.label`} mode="capitalize" plural="true" /></span>
                          <ActiveFiltersCounter name={'labels'} />
                        </React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <LocalLabels />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {!dashboard.isCsv &&
                    areMembersAllowed(this.props.hasMembers, this.props.dashboard) &&
                    disable.indexOf('members') === -1 && (
                      <Accordion.Item eventKey="members"
                        uuid="members">
                        <Accordion.Header>
                          <React.Fragment>
                            <span className='title'><FormattedMessage id={`${fileApp || app}.member`} mode="capitalize" plural="true" /></span>
                            <ActiveFiltersCounter name={'members'} />
                          </React.Fragment>
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className='member_list'>
                            <LocalMembers />
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    )}

                  {disable.indexOf('customFields') === -1 &&
                    customFields
                      .filter(cf => {
                        return !!_.find(cf.options, o => {
                          return !o.history && o._id !== 'no-option';
                        });
                      })
                      .map((cf, i) => {
                        return (
                          <Accordion.Item
                            key={i}
                            uuid={`cf_${i}`}
                            cf_key={`cf_${i}`}
                            eventKey={this.state.allExpanded.indexOf('cf_') > -1 ? 'cf_' : `cf_${i}`}
                          >
                            <Accordion.Header>
                              <React.Fragment>
                                <span className='title'>{cf.name}</span>
                                <ActiveFiltersCounter name={cf.extId} isCf={true} />
                              </React.Fragment>
                            </Accordion.Header>
                            <Accordion.Body>
                              <LocalCustomField id={cf.extId} name={cf.name} />
                            </Accordion.Body>
                          </Accordion.Item>
                        );
                      })}
                </Accordion>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          :
          <Accordion alwaysOpen='true' className='filter filter-panel filter-panel-static' activeKey={this.state.customizeExpanded}>
            <Accordion.Item
              uuid="customizeControls"
              eventKey="customizeControls"
              activeKey={this.state.customizeExpanded}
            >
              <Accordion.Header>
                <React.Fragment>
                  <span className="title">Customize view</span>
                </React.Fragment>
              </Accordion.Header>
              <Accordion.Body>
                <Accordion alwaysOpen activeKey={['widgets']}>
                  <Accordion.Item
                      uuid="widgets"
                      eventKey="widgets"
                    >
                      <Accordion.Header>
                        <React.Fragment>Widgets</React.Fragment>
                      </Accordion.Header>
                      <Accordion.Body>
                        <WidgetSelector
                          isProcessImprovement={this.props.isProcessImprovement}
                          customizeMode={this.props.customizeMode}
                          onTempWidgetSelectionChange={this.props.onTempWidgetSelectionChange}
                          tempSelectedWidgets={this.props.tempSelectedWidgets}
                          availableWidgets={this.props.availableWidgets}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
              </Accordion.Body>
            </Accordion.Item>

          </Accordion>
        }
        {isOverview &&
          <div className='filter-action-bth'>
            {this.props.customizeMode ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant='outline-secondary' onClick={this.handleGoBackFromCustomize}>
                  Back
                </Button>
                <Button variant='primary' onClick={this.handleApplyCustomization}>
                  Apply
                </Button>
              </div>
            ) : (
              <Button className='' onClick={this.handleCustomizeViewClick}>
                Customize view
              </Button>
            )}
          </div>
        }
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    handleError: err => dispatch(handleError(err, { log: false })),
    saveGlobalConfig: () => dispatch(saveGlobalConfig()),
    saveConfig: () => dispatch(saveConfig()),
    initFilters: (disable, isProcessImprovement) => dispatch(initFilters(disable, isProcessImprovement)),
    updateValues: (field, isProcessImprovement) => dispatch(updateValues(field, isProcessImprovement)),
    saveFilterSize: size => dispatch(saveFilterSize(size)),

    updateEnableZero: enabled => dispatch(updateEnableZero(enabled)),
    updateEnabledColors: enabled => dispatch(updateEnabledColors(enabled)),
    updateWipStagesTooltip: val => dispatch(updateWipStagesTooltip(val)),
    updateApproxAvgStagesTooltip: val => dispatch(updateApproxAvgStagesTooltip(val)),
    updateArrivalDepartureLines: val => dispatch(updateArrivalDepartureLines(val)),
    updateTrendLine: val => dispatch(updateTrendLine(val)),
    updateSelectedDate: val => dispatch(updateSelectedDate(val)),
    updateExcludeWeekends: val => dispatch(updateExcludeWeekends(val)),
    updateActivePanel: val => dispatch(updateActivePanel(val)),
    updateDueDateTasks: val => dispatch(updateDueDateTasks(val)),
    updateExpandedFilters: vals => dispatch(updateExpandedFilters(vals)),
    updateWorkStart: val => dispatch(updateWorkHours(false, val)),
    updateWorkEnd: val => dispatch(updateWorkHours(true, val)),
    updateHolidays: val => dispatch(updateHolidays(val)),
    updateShowColumns: val => dispatch(updateShowColumns(val, 'cta')),
    updateBlockers: val => dispatch(updateBlockers(val)),
    updateBlockedTime: val => dispatch(updateBlockedTime(val))
  };
};

export const mapStateToProps = state => {
  let chart = state.filters.chart;
  return {
    app: state.app.app,
    filtersDataHash: state.app.filtersDataHash,
    updatedField: state.app.updatedField,
    user: state.auth.user,
    dashboard: state.dashboards.selected || {},
    dashboardsCount: state.auth.user.dashboards,
    chart,
    customFields: state.app.data && state.app.data.customFields ? state.app.data.customFields : [],
    customFilterKeys: state.app.data && state.app.data.customFields ? state.app.data.customFields.map((cf, i) => { return `cf_${i}` }) : [],
    hasMembers: !!state.filters.allMembers && !!state.filters.allMembers.length,
    excludeZeroDays: state.filters[chart].excludeZeroDays,
    dateFormat: state.filters.dateFormat,
    wipStagesTooltip: state.filters[chart].wipStagesTooltip,
    approxAvgStagesTooltip: state.filters[chart].approxAvgStagesTooltip,
    arrivalDepartureLines: state.filters[chart].arrivalDepartureLines,
    trendLine: state.filters[chart].trendLine,
    selectedDate: state.filters[chart].selectedDate,
    dueDateTasks: state.filters[chart].dueDateTasks,
    expanded: state.filters[chart].expandedFilters,
    excludeWeekends: state.filters.excludeWeekends,
    activePanel: state.filters.activePanel,
    workStart: state.filters.workStart,
    workEnd: state.filters.workEnd,
    holidays: state.filters.holidays,
    showColumns: state.filters.cta.showColumns,
    blockers: state.filters[chart].blockers
  };
};

export default windowSize(connect(mapStateToProps, mapDispatchToProps)(Filter));
